import React from "react";
import Select from "react-select";
import { InputGroup } from "reactstrap";

/** A user-select option for `authorname`: the user's name is both label and value. */
export type AuthorOption = { label: string; value: string };

export type ContentStringFieldProps = {
  settingVar: { variable: string; value?: string };
  options?: AuthorOption[];
  onChange: (name: string, value: string) => void;
  dateDisabled?: boolean;
  maxLength?: number;
};

/**
 * Content-mode String building block (SCRUM-641, VariableField registry).
 *
 * The content screens special-case two String variables by NAME (pinned by
 * SCRUM-630/640): `authorname` renders a user select that stores the user
 * NAME STRING (not the doc), and `date` renders an HTML date input storing
 * the ISO date string. Every other String variable renders the content
 * screens' 5-row textarea (the settings side keeps StringField's single-line
 * input — pinned per surface by SCRUM-630/640).
 *
 * These cases exist ONLY on the content side — the settings screens render a
 * plain String input for the same names — which is why this block lives in
 * CONTENT_OVERRIDES instead of inside StringField.
 *
 * Props beyond the block contract:
 *  - `options`: the user list for `authorname` ({label: name, value: name}),
 *    injected by the host (which owns any role filtering — the wizard offers
 *    CONTENTEDITOR users only, the update screens all users).
 *  - `dateDisabled`: the update-Required-only quirk that disables the date
 *    input for midnightExpress* templates; the host computes it. Defaults to
 *    false (wizard + Optional column: never disabled).
 *  - `maxLength`: the template author's character cap for the plain textarea
 *    (the variable's `char_number`). Only the wizard's Required column passes
 *    it (its legacy textarea carried the attribute — SCRUM-643 review fix);
 *    the update screens and the wizard Optional column never capped.
 */
export default function ContentStringField({
  settingVar,
  options = [],
  onChange,
  dateDisabled = false,
  maxLength = undefined,
}: ContentStringFieldProps) {
  if (settingVar.variable === "authorname") {
    return (
      <Select
        value={options.find((option) => option.value === settingVar.value) ?? null}
        name={settingVar.variable}
        options={options}
        onChange={(sel) => {
          // react-select hands `null` on a cleared selection; the picker is not
          // clearable, so ignoring it is the no-op that keeps the old value.
          if (!sel) return;
          onChange(settingVar.variable, sel.value);
        }}
        isSearchable={true}
      />
    );
  }

  if (settingVar.variable === "date") {
    return (
      <input
        type="date"
        className="form-control"
        name={settingVar.variable}
        value={settingVar.value || ""}
        disabled={dateDisabled}
        onChange={(e) => onChange(settingVar.variable, e.target.value)}
      />
    );
  }

  return (
    <InputGroup className="input-group-merge">
      <textarea
        rows={5}
        cols={70}
        className="textarea"
        placeholder={`insert value for ${settingVar.variable}`}
        value={settingVar.value || ""}
        name={settingVar.variable}
        maxLength={maxLength}
        onChange={(e) => onChange(settingVar.variable, e.target.value)}
      />
    </InputGroup>
  );
}
